/**
 * LeadMeetingsWidget — inline meetings context for Lead Focus / Contact Focus.
 *
 * Consumes `GET /api/meetings?lead_id=<id>` (existing endpoint, no new API).
 * Works unchanged for contacts via the `contactId` prop → uses `contact_id` filter.
 *
 * States: loading skeleton · empty · error (with retry) · data (next + last).
 * Role gating: read_only sees the widget + Open/View-all, but NOT Schedule.
 */
import { useEffect, useState } from "react";
import { useAuth } from "@/App";
import { listMeetings } from "@/lib/meetingsApi";
import { Video, ExternalLink, CalendarPlus, ChevronRight, AlertCircle } from "lucide-react";

const WRITE_ROLES = new Set(["super_admin", "crm_admin", "sales_manager", "sales_agent"]);

const BADGE = {
  scheduled: { bg: "#E6F2FF", fg: "#1A73E8", label: "Scheduled" },
  completed: { bg: "#DAFBE8", fg: "#1D9E75", label: "Completed" },
  canceled:  { bg: "#EEE",    fg: "#666",    label: "Canceled" },
  no_show:   { bg: "#FFF1F0", fg: "#A32D2D", label: "No-show" },
};

function StatusBadge({ status }) {
  const s = BADGE[status] || { bg: "#EEE", fg: "#444", label: status };
  return (
    <span data-testid={`lmw-status-${status}`}
      style={{ fontSize: 10, fontWeight: 600, padding: "2px 6px", borderRadius: 3,
               background: s.bg, color: s.fg }}>
      {s.label}
    </span>
  );
}

function fmt(dt) {
  if (!dt) return "—";
  try { return new Date(dt).toLocaleString(); } catch { return dt; }
}

function pickNextAndLast(items) {
  // `items` come newest-scheduled-first from the API (sort: scheduled_at desc).
  // `next`  = nearest upcoming meeting with status=scheduled
  // `last`  = most recent non-scheduled (completed / canceled / no_show) row
  const nowIso = new Date().toISOString();
  const scheduledFuture = items
    .filter(m => m.status === "scheduled" && (m.scheduled_at || "") >= nowIso)
    .sort((a, b) => (a.scheduled_at || "").localeCompare(b.scheduled_at || ""));
  const next = scheduledFuture[0] || null;
  const last = items.find(m => m.status !== "scheduled") || null;
  return { next, last };
}

export function LeadMeetingsWidget({ leadId, contactId }) {
  const { user } = useAuth();
  const role = user?.role || "";
  const canWrite = WRITE_ROLES.has(role);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);

  const scopeId = leadId || contactId;
  const scopeKey = leadId ? "lead_id" : "contact_id";

  const load = async () => {
    if (!scopeId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await listMeetings({ [scopeKey]: scopeId, limit: 25 });
      setItems(r?.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load meetings");
      setItems([]);
    }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [scopeId]);

  if (!scopeId) return null;

  const viewAllHref = `/meetings?${scopeKey}=${encodeURIComponent(scopeId)}`;
  const scheduleHref = `/meetings?${scopeKey}=${encodeURIComponent(scopeId)}&schedule=1`;

  const { next, last } = pickNextAndLast(items);

  return (
    <div style={{ marginBottom: 14, border: "0.5px solid #e5e7eb", borderRadius: 8, padding: 12, background: "#fff" }}
         data-testid="lead-meetings-widget">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Video style={{ width: 13, height: 13, color: "#1A73E8" }} />
          <span style={{ fontSize: 12, fontWeight: 650 }}>Meetings</span>
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {canWrite && (
            <a className="btn" href={scheduleHref} style={{ fontSize: 10, display: "inline-flex", gap: 4, alignItems: "center" }}
               data-testid="lmw-schedule">
              <CalendarPlus style={{ width: 10, height: 10 }} /> Schedule
            </a>
          )}
          <a className="btn" href={viewAllHref} style={{ fontSize: 10, display: "inline-flex", gap: 4, alignItems: "center" }}
             data-testid="lmw-view-all">
            View all <ChevronRight style={{ width: 10, height: 10 }} />
          </a>
        </div>
      </div>

      {loading && (
        <div data-testid="lmw-loading"
             style={{ display: "flex", gap: 10, padding: "4px 0" }}>
          <div style={{ height: 14, width: 180, background: "#eee", borderRadius: 3 }} />
          <div style={{ height: 14, width: 120, background: "#eee", borderRadius: 3 }} />
        </div>
      )}

      {!loading && error && (
        <div data-testid="lmw-error"
             style={{ padding: 8, fontSize: 11, color: "#A32D2D", background: "#FFF1F0",
                      borderRadius: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span><AlertCircle style={{ width: 11, height: 11, display: "inline", marginRight: 4 }} />{error}</span>
          <button className="btn" onClick={load} style={{ fontSize: 10 }} data-testid="lmw-retry">Retry</button>
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div data-testid="lmw-empty"
             style={{ padding: 16, fontSize: 11, color: "#888", textAlign: "center" }}>
          No meetings yet for this lead.
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {/* Next */}
          <div data-testid="lmw-next"
               style={{ padding: 8, background: "#F7F9FC", borderRadius: 6 }}>
            <div style={{ fontSize: 9, color: "#888", textTransform: "uppercase", fontWeight: 600, marginBottom: 4 }}>Next</div>
            {next ? (
              <>
                <div style={{ fontSize: 12, fontWeight: 600 }}>{next.title}</div>
                <div style={{ fontSize: 10, color: "#666", margin: "2px 0 6px" }}>
                  {fmt(next.scheduled_at)} · {next.duration_min}m
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <StatusBadge status={next.status} />
                  {next.join_url && (
                    <a href={next.join_url} target="_blank" rel="noreferrer"
                       className="btn" style={{ fontSize: 10, display: "inline-flex", gap: 3, alignItems: "center" }}
                       data-testid={`lmw-open-${next.id}`}>
                      <ExternalLink style={{ width: 10, height: 10 }} /> Open
                    </a>
                  )}
                </div>
              </>
            ) : (
              <div style={{ fontSize: 11, color: "#888" }} data-testid="lmw-next-empty">No upcoming meetings</div>
            )}
          </div>
          {/* Last */}
          <div data-testid="lmw-last"
               style={{ padding: 8, background: "#F7F9FC", borderRadius: 6 }}>
            <div style={{ fontSize: 9, color: "#888", textTransform: "uppercase", fontWeight: 600, marginBottom: 4 }}>Last outcome</div>
            {last ? (
              <>
                <div style={{ fontSize: 12, fontWeight: 600 }}>{last.title}</div>
                <div style={{ fontSize: 10, color: "#666", margin: "2px 0 6px" }}>
                  {fmt(last.updated_at || last.scheduled_at)}
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <StatusBadge status={last.status} />
                  {last.outcome && (
                    <span style={{ fontSize: 10, color: "#666", fontStyle: "italic" }}>— {last.outcome.slice(0, 40)}{last.outcome.length > 40 ? "…" : ""}</span>
                  )}
                </div>
              </>
            ) : (
              <div style={{ fontSize: 11, color: "#888" }} data-testid="lmw-last-empty">No past meetings</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default LeadMeetingsWidget;
